import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { logoutUser } from "actions";
import { closeModal } from "actions/modal";
import PropTypes from "prop-types";
import { User } from "resourceModels";
import { getResource } from "selectors/resources";
import Button from "components/Common/Button";
import moment from "moment";
import "./style.scss";
import {
  EXPIRE_5_MIN, EXPIRE_5_SECS, PASSWORD_TTL_DAYS, TIME_TO_AUTO_LOGOUT_MS
} from "constants/warnings";
import DHDate from "services/dates";
import { modalKeepOpen } from "./actions";

const EXPIRED_TEXT = (
  <div className="ModalExpiredPassword__details ModalExpiredPassword__expired">
    <p>Your password has expired. You will be automatically logged out shortly.</p>
    <p>Please re-login and follow instructions.</p>
  </div>
);

export class ModalExpiredPassword extends React.Component {
  showProfileModalBound = this.showProfileModal.bind(this);

  static propTypes = {
    user: PropTypes.instanceOf(User).isRequired,
    onCloseModal: PropTypes.func.isRequired,
    modalLogoutUser: PropTypes.func.isRequired,
    setState: PropTypes.func.isRequired,
    makeUnclosable: PropTypes.func.isRequired
  };

  /**
   * Converts timestamp from backend into human readable string and determines
   * whether expired text should be shown
   * @returns {Object} contains human readable date and expired flag
   */
  getTimeToExpire() {
    const { makeUnclosable, modalLogoutUser, user } = this.props;
    const { date } = new DHDate(user.passwordCreatedDate);
    const timeleft = moment.utc(date).add(PASSWORD_TTL_DAYS, "days");

    if (timeleft.diff(moment.utc(), "seconds") <= EXPIRE_5_SECS) {
      makeUnclosable();

      // Set timeout to automatically boot out user
      setTimeout(() => {
        modalLogoutUser();
      }, TIME_TO_AUTO_LOGOUT_MS);

      return { expired: true, timeleft: timeleft.fromNow(true) };
    }


    if (timeleft.diff(moment.utc(), "minutes") <= EXPIRE_5_MIN) {
      return { expired: false, timeleft: timeleft.fromNow(true) };
    }

    return { expired: false, timeleft: timeleft.format("LLL") };
  }

  /**
   * Shows the Profile Modal
   */
  showProfileModal() {
    const { setState } = this.props;

    setState("MODAL", {
      isOpen: true,
      view: "MODAL_PROFILE",
      modalProps: {}
    });
  }

  /**
   * Returns the valid warning text
   * @returns {String} Warning message
   */
  countdownToExpireText() {
    const { timeleft } = this.getTimeToExpire();

    return (
      <div className="ModalExpiredPassword__details ModalExpiredPassword__countdown">
        {
          timeleft.includes("minute") || timeleft.includes("seconds")
            ? (
              <p className="ModalExpiredPassword__warning_minutes_left">
                Your password will expire in <b>{timeleft}</b>. You will be
                automatically logged out and any unsaved changes will be lost.
              </p>
            )
            : (
              <p className="ModalExpiredPassword__warning_day_left">
                Your password will expire on <b>{timeleft}</b>. Please update
                your password as soon as possible
              </p>
            )
        }
        <p>Do you want to change it now?</p>
      </div>
    );
  }

  /**
   * renders the component
   * @returns {React.Component} ModalExpiredPassword
   */
  render() {
    const {
      modalLogoutUser,
      onCloseModal
    } = this.props;

    const { expired } = this.getTimeToExpire();

    return (
      <div className="ModalExpiredPassword Modal_modal">
        <h5 className="Modal__title">{expired ? "Password Expired" : "Update Password"}</h5>
        <div className="Modal__content">
          <section className="Modal__section ModalExpiredPassword__details">
            {
              expired
                ? EXPIRED_TEXT
                : this.countdownToExpireText()
            }
          </section>
        </div>
        {expired
          ? (
            <div className="ModalExpiredPassword__bottom">
              <Button
                text="Log Out Now"
                stretched
                lightPrimary
                onClick={modalLogoutUser}
                disabledStyleFlat
                customClassName="ModalExpiredPassword__submit
                ModalExpiredPassword__button ModalExpiredPassword__logout"
              />
            </div>
          )
          : (
            <div>
              <div className="ModalExpiredPassword__bottom">
                <Button
                  text="Update Password"
                  stretched
                  lightPrimary
                  onClick={this.showProfileModalBound}
                  disabledStyleFlat
                  customClassName="ModalExpiredPassword__button
                  ModalExpiredPassword__action ModalExpiredPassword__update
                  ModalExpiredPassword__submit"
                />
              </div>
              <div className="ModalExpiredPassword__bottom">
                <Button
                  text="Not Now"
                  stretched
                  light
                  onClick={onCloseModal}
                  disabledStyleFlat
                  customClassName="ModalExpiredPassword__button
                  ModalExpiredPassword__action ModalExpiredPassword__notnow"
                />
              </div>
            </div>
          )}
      </div>
    );
  }
}

ModalExpiredPassword.isSmall = true;

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapState(state) {
  return {
    user: getResource(state, "user")
  };
}

/**
 * @param {Function} dispatch
 * @returns {Object}
 */
function mapDispatch(dispatch) {
  return {
    onCloseModal: bindActionCreators(closeModal, dispatch),
    modalLogoutUser: bindActionCreators(logoutUser, dispatch),
    makeUnclosable: bindActionCreators(modalKeepOpen, dispatch)
  };
}

const Connector = connect(
  mapState,
  mapDispatch
)(ModalExpiredPassword);

export default Connector;
